import { Suspense, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getSessions, postCreateSession, postCreateStudentPracticeSession } from '../api/tutor';
import { adminGetHealthAlerts } from '../api/admin';
import { getStoredUser, parseJsonSafe } from '../api/client';
import { formatSessionStatus, formatSkill } from '../lib/format';
import { getSessionPath } from '../lib/sessionRouting';
import ActiveSessionConflictPrompt from '../components/ActiveSessionConflictPrompt';
import useActiveSessionConflict, {
  createSessionRequestError,
} from '../components/useActiveSessionConflict';
import SessionLengthSelector from '../components/SessionLengthSelector';
import { getMyStudentDashboard } from '../api/me';
import { shouldShowDebugUI } from '../components/debug/debugGate';
import { getRecentSessionSummary, getRecentSessionTitle } from '../utils/sessionSummaries';
import { buildSessionLengthPayload, DEFAULT_SESSION_LENGTH } from '../utils/sessionLength';
import { DebugUnlock } from '../generated/debugToolLoaders';

function getDashboardHero(role) {
  switch (role) {
    case 'ADMIN':
      return {
        title: 'Operations overview',
        subtitle: 'Track readiness, move onboarding forward, and keep staff operations inside the supported backend surface.',
      };
    case 'TUTOR':
      return {
        title: 'Teaching workspace',
        subtitle: 'Move between linked students, active sessions, and onboarding follow-up without leaving the core workflow.',
      };
    case 'PARENT':
      return {
        title: 'Family overview',
        subtitle: 'Check linked students, recent activity, and the weekly practice direction that is safe for parent review.',
      };
    case 'STUDENT':
      return {
        title: 'Ready for today’s practice?',
        subtitle: 'Your next step, progress, and weekly focus are here.',
      };
    default:
      return {
        title: 'Dashboard',
        subtitle: 'Your role-specific workspace appears here when your account is fully configured.',
      };
  }
}

function mapMetricTone(tone) {
  switch (tone) {
    case 'accent':
    case 'success':
    case 'warning':
    case 'danger':
      return tone;
    default:
      return 'neutral';
  }
}

function formatFocusAreaForSentence(value) {
  const text = String(value || '').trim();
  if (!text) {
    return '';
  }
  return text.charAt(0).toLowerCase() + text.slice(1);
}

function normaliseStudentStat(stat) {
  const key = String(stat?.key || '').toLowerCase();
  if (key === 'sessions') {
    return { ...stat, label: 'Recent sessions' };
  }
  if (key === 'accuracy') {
    return { ...stat, label: 'Accuracy (last 7 days)' };
  }
  if (key === 'streak') {
    return { ...stat, label: 'Practice streak' };
  }
  return stat;
}

function getDashboardMetrics({ role, sessions, healthAlertBadge, studentDashboard }) {
  if (role === 'STUDENT' && studentDashboard?.useful_analytics?.stats?.length) {
    return studentDashboard.useful_analytics.stats.slice(0, 3).map((stat) => {
      const normalisedStat = normaliseStudentStat(stat);
      return {
        label: normalisedStat.label,
        value: normalisedStat.value,
        tone: mapMetricTone(stat.tone),
      };
    });
  }

  const metrics = [
    {
      label: 'Recent sessions',
      value: String(sessions.length),
      tone: 'neutral',
    },
  ];

  if (role === 'ADMIN') {
    metrics.push({
      label: 'Health status',
      value: healthAlertBadge || 'Stable',
      tone: healthAlertBadge === 'CRITICAL' ? 'danger' : healthAlertBadge ? 'warning' : 'success',
    });
  }

  if (role === 'TUTOR') {
    metrics.push({
      label: 'Primary flow',
      value: 'My students',
      tone: 'accent',
    });
  }

  if (role === 'PARENT') {
    metrics.push({
      label: 'View type',
      value: 'Read-only',
      tone: 'neutral',
    });
  }

  return metrics;
}

function getSessionStatusTone(status) {
  if (status === 'COMPLETED') {
    return 'weekly-practice-plan-pill-complete';
  }
  if (status === 'ABANDONED') {
    return 'weekly-practice-plan-pill-not-started';
  }
  return 'weekly-practice-plan-pill-progress';
}

function studentPlanStatusLabel(status) {
  if (status === 'COMPLETE') {
    return 'Complete';
  }
  if (status === 'IN_PROGRESS') {
    return 'In progress';
  }
  return 'Not started';
}

function skillBucketToneClass(tone) {
  if (tone === 'success') {
    return 'dashboard-skill-bucket-success';
  }
  if (tone === 'warning') {
    return 'dashboard-skill-bucket-warning';
  }
  return 'dashboard-skill-bucket-accent';
}

function buildStudentDetailItems(studentDashboard) {
  const items = [];
  const studentName = String(studentDashboard?.student_display_name || '').trim();
  const schoolYear = String(
    studentDashboard?.school_year
    || studentDashboard?.student_school_year
    || studentDashboard?.year_group
    || ''
  ).trim();
  const tutorName = String(studentDashboard?.tutor?.name || '').trim();
  const parentName = String(
    studentDashboard?.parent_contact_name
    || studentDashboard?.parent_name
    || studentDashboard?.contact_name
    || ''
  ).trim();

  if (studentName) {
    items.push({ key: 'student', label: 'Student', value: studentName });
  }
  if (schoolYear) {
    items.push({ key: 'school_year', label: 'School year', value: schoolYear });
  }
  if (tutorName) {
    items.push({ key: 'tutor', label: 'Tutor', value: tutorName });
  }
  if (parentName) {
    items.push({ key: 'parent', label: 'Parent contact', value: parentName });
  }
  return items;
}

function buildStudentProgressCopy(skillProgress, nextAction) {
  if (!skillProgress) {
    return null;
  }

  const buckets = Array.isArray(skillProgress.buckets) ? skillProgress.buckets : [];
  const secureCount = Number(buckets.find((bucket) => bucket?.key === 'secure')?.count || 0);
  const inProgressCount = Number(buckets.find((bucket) => bucket?.key === 'in_progress')?.count || 0);
  const reviewCount = Number(buckets.find((bucket) => bucket?.key === 'needs_review')?.count || 0);
  const focusArea = String(nextAction?.focus_area || '').trim();

  let headline = skillProgress.headline || 'Your progress is moving.';
  let detail = skillProgress.detail || '';

  if (inProgressCount > 0 && reviewCount > 0) {
    headline = 'You’re maintaining strong skills, but one area needs review.';
    detail = focusArea
      ? `${focusArea} is still improving, and ${reviewCount} skill${reviewCount === 1 ? ' needs' : 's need'} a closer look.`
      : `${inProgressCount} skills are moving forward, and ${reviewCount} skill${reviewCount === 1 ? ' needs' : 's need'} a closer look.`;
  } else if (inProgressCount > 0 && secureCount > 0) {
    headline = 'You’re building consistency.';
    detail = `${secureCount} skill${secureCount === 1 ? ' feels' : 's feel'} secure, and ${inProgressCount} ${inProgressCount === 1 ? 'is' : 'are'} still in active practice.`;
  } else if (secureCount > 0) {
    headline = 'You’re keeping strong skills secure.';
    detail = `${secureCount} skill${secureCount === 1 ? ' is' : 's are'} holding up well right now.`;
  }

  return {
    ...skillProgress,
    headline,
    detail,
  };
}

function buildStudentWeeklyPlan(weeklyPlan, nextAction) {
  if (!weeklyPlan) {
    return null;
  }

  const remainingCount = Math.max(0, Number(weeklyPlan.total_count || 0) - Number(weeklyPlan.completion_count || 0));
  const focusArea = String(nextAction?.focus_area || '').trim();
  const items = Array.isArray(weeklyPlan.items)
    ? weeklyPlan.items.map((item) => {
      const isCurrent = item?.key === weeklyPlan.current_item_key;
      const isNext = item?.key === weeklyPlan.next_item_key;
      let detail = item?.detail || '';

      if (isCurrent && focusArea) {
        detail = `Continue ${formatFocusAreaForSentence(focusArea)} in your next session.`;
      } else if (isNext && item?.skill_labels?.[0]) {
        detail = `After that, give ${item.skill_labels[0]} another pass to keep it secure.`;
      }

      return {
        ...item,
        detail,
      };
    })
    : [];

  let forwardCopy = 'This week is on track.';
  if (remainingCount > 0) {
    forwardCopy = `Current focus items needing attention this week: ${remainingCount}.`;
  } else if (Number(weeklyPlan.total_count || 0) > 0) {
    forwardCopy = 'You’ve completed this week’s core practice.';
  }

  return {
    ...weeklyPlan,
    progress_summary: `${weeklyPlan.completion_count} of ${weeklyPlan.total_count} current focus items complete`,
    forward_copy: forwardCopy,
    items,
  };
}

function DashboardPage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [studentDashboard, setStudentDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [createErrorMessage, setCreateErrorMessage] = useState('');
  const [studentPracticeErrorMessage, setStudentPracticeErrorMessage] = useState('');
  const [studentDashboardView, setStudentDashboardView] = useState('overview');
  const [healthAlertBadge, setHealthAlertBadge] = useState('');
  const [isCreatingStudentPractice, setIsCreatingStudentPractice] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [studentId, setStudentId] = useState('');
  const [tutorUserId, setTutorUserId] = useState('');
  const [track, setTrack] = useState('DOUBLE_AWARD_2025');
  const [tier, setTier] = useState('FOUNDATION');
  const [unit, setUnit] = useState('U1');
  const [topicId, setTopicId] = useState('');
  const [sessionLength, setSessionLength] = useState(DEFAULT_SESSION_LENGTH);
  const user = getStoredUser();
  const role = user?.role || null;
  const isAdmin = role === 'ADMIN';
  const isStudent = role === 'STUDENT';
  const canRenderDebug = isAdmin && shouldShowDebugUI();
  const hero = isStudent && studentDashboard?.greeting
    ? {
      title: studentDashboard.greeting.title,
      subtitle: studentDashboard.greeting.subtitle,
    }
    : getDashboardHero(role);
  const metrics = getDashboardMetrics({ role, sessions, healthAlertBadge, studentDashboard });
  const recentSessions = isStudent
    ? (Array.isArray(studentDashboard?.recent_sessions) ? studentDashboard.recent_sessions : [])
    : sessions.slice(0, 6);

  useEffect(() => {
    let isMounted = true;

    const loadDashboard = async () => {
      try {
        if (isStudent) {
          const response = await getMyStudentDashboard();
          const data = await parseJsonSafe(response);
          if (
            response.status === 403
            && data?.detail === 'guardian_verification_required'
          ) {
            navigate('/access-restricted?reason=guardian', { replace: true });
            return;
          }
          if (!response.ok) {
            throw new Error(
              (typeof data?.detail === 'string' && data.detail)
              || (typeof data?.message === 'string' && data.message)
              || 'Failed to load dashboard.'
            );
          }
          if (isMounted) {
            setStudentDashboard(data);
          }
          return;
        }

        const response = await getSessions();
        const data = await parseJsonSafe(response);
        if (
          response.status === 403
          && data?.detail === 'guardian_verification_required'
        ) {
          navigate('/access-restricted?reason=guardian', { replace: true });
          return;
        }
        if (!response.ok) {
          throw new Error('Failed to load sessions.');
        }
        if (isMounted) {
          setSessions(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'Failed to load dashboard.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      isMounted = false;
    };
  }, [isStudent, navigate]);

  useEffect(() => {
    let isMounted = true;
    if (!isAdmin) {
      setHealthAlertBadge('');
      return () => {
        isMounted = false;
      };
    }

    const loadAlertBadge = async () => {
      try {
        const response = await adminGetHealthAlerts({ hours: 24 });
        const payload = await parseJsonSafe(response);
        if (!response.ok) {
          return;
        }
        const alerts = Array.isArray(payload?.alerts) ? payload.alerts : [];
        const severities = new Set(
          alerts
            .map((item) => String(item?.severity || '').toUpperCase())
            .filter(Boolean)
        );
        let badge = '';
        if (severities.has('CRITICAL')) {
          badge = 'CRITICAL';
        } else if (severities.has('ERROR')) {
          badge = 'ERROR';
        } else if (severities.has('WARN')) {
          badge = 'WARN';
        }
        if (isMounted) {
          setHealthAlertBadge(badge);
        }
      } catch (_error) {
        if (isMounted) {
          setHealthAlertBadge('');
        }
      }
    };

    loadAlertBadge();
    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  // Every session-creation entry point owes the learner the same two choices on
  // an ACTIVE_SESSION_EXISTS 409. Both dashboard create sites use the shared
  // implementation so neither can drift from practice or My Students.
  const adminSessionConflict = useActiveSessionConflict({
    navigate,
    onEndError: (error) => setCreateErrorMessage(error?.message || 'Failed to end the existing session.'),
    onRetryError: (error) => setCreateErrorMessage(error?.message || 'Failed to create tutor session.'),
  });

  const handleStudentPracticeError = (error) => {
    const detailRaw = String(error?.detail || error?.message || '');
    const status = Number(error?.status);
    if (
      status === 403
      && (
        error?.detail === 'guardian_verification_required'
        || detailRaw.includes('guardian_verification_required')
      )
    ) {
      navigate('/access-restricted?reason=guardian', { replace: true });
      return;
    }
    if (status === 400 || status === 403) {
      // The recommended skill was not launchable in beta (e.g. wrong tier or
      // not a beta-ready family). Fall back to the topic picker so the student
      // can choose a valid topic, rather than a dead-end "try again" that
      // would just repeat the same scope failure. See issue #677.
      navigate('/practice');
      return;
    }
    setStudentPracticeErrorMessage('Could not start practice right now. Please try again.');
  };

  const studentSessionConflict = useActiveSessionConflict({
    navigate,
    onEndError: () =>
      setStudentPracticeErrorMessage('Could not end your existing session. Please try again.'),
    onRetryError: handleStudentPracticeError,
  });

  const handleCreateTutorSession = async (event) => {
    event.preventDefault();
    setCreateErrorMessage('');
    if (!isAdmin) {
      return;
    }

    if (!/^\d+$/.test(studentId.trim())) {
      setCreateErrorMessage('Student id must be numeric.');
      return;
    }

    if (!/^\d+$/.test(tutorUserId.trim())) {
      setCreateErrorMessage('Tutor user id must be numeric.');
      return;
    }

    const parsedTopicId = topicId.trim() ? Number(topicId) : null;
    if (topicId.trim() && !Number.isFinite(parsedTopicId)) {
      setCreateErrorMessage('Topic id must be numeric when provided.');
      return;
    }

    const payload = {
      student_id: Number(studentId),
      tutor_user_id: Number(tutorUserId),
      exam_mode: false,
      track: track.trim(),
      tier,
      unit: unit.trim(),
      ...buildSessionLengthPayload(sessionLength),
    };
    if (parsedTopicId !== null) {
      payload.topic_id = parsedTopicId;
    }

    // Closed over the exact payload the admin submitted, so "End it and start a
    // new session" retries that request rather than a re-derived approximation.
    const createSession = async () => {
      setIsCreatingSession(true);
      try {
        const response = await postCreateSession(payload);
        const data = await parseJsonSafe(response);
        if (!response.ok) {
          throw createSessionRequestError(response, data, 'Failed to create tutor session.');
        }

        const createdSessionId = data?.session_id ?? data?.id;
        if (!createdSessionId) {
          throw new Error('Session created but id was missing from response.');
        }

        navigate(`/sessions/${createdSessionId}/tutor`);
      } finally {
        setIsCreatingSession(false);
      }
    };

    try {
      await adminSessionConflict.guardCreate(createSession);
    } catch (error) {
      setCreateErrorMessage(error.message || 'Failed to create tutor session.');
    }
  };

  const handleStartStudentPractice = async () => {
    setStudentPracticeErrorMessage('');
    const recommendedSkillCode = String(
      studentDashboard?.next_action?.recommended_skill_code || ''
    ).trim();
    if (!recommendedSkillCode) {
      // No beta-ready skill to scope a first session to (e.g. a brand-new
      // student with no plan yet). Send them to the topic picker rather than a
      // whole-unit mix: unit mix is rejected by beta topic-gating (the backend
      // _validate_unit_mix_selection returns 400 in prod-like environments), so
      // the previous default surfaced as "Could not start practice".
      navigate('/practice');
      return;
    }
    const payload = {
      ...sessionLength,
      selectionMode: 'single_topic',
      skillCode: recommendedSkillCode,
    };

    // Closed over the exact payload, so "End it and start a new session" retries
    // the original request.
    const createSession = async () => {
      setIsCreatingStudentPractice(true);
      try {
        const data = await postCreateStudentPracticeSession(payload);
        const createdSessionId = data?.session_id ?? data?.id;
        if (!createdSessionId) {
          throw new Error('Session created but id was missing from response.');
        }
        navigate(`/sessions/${createdSessionId}/tutor`);
      } finally {
        setIsCreatingStudentPractice(false);
      }
    };

    try {
      await studentSessionConflict.guardCreate(createSession);
    } catch (error) {
      handleStudentPracticeError(error);
    }
  };

  const renderStudentDashboard = () => {
    const nextAction = studentDashboard?.next_action;
    const activeSession = recentSessions.find(
      (session) => String(session?.status || '').trim().toUpperCase() === 'ACTIVE'
    );
    const displayedNextAction = activeSession
      ? {
        title: 'Resume your practice',
        focus_area: activeSession.focus_label || activeSession.label || 'Current practice',
        reason: 'Continue where you left off. Your in-progress work is still waiting for you.',
        priority_label: 'In progress',
        cta_label: 'Resume session',
      }
      : nextAction;
    const skillProgress = buildStudentProgressCopy(studentDashboard?.skill_progress, nextAction);
    const weeklyPlan = buildStudentWeeklyPlan(studentDashboard?.weekly_plan, nextAction);
    const visibleRecentSessions = recentSessions.slice(0, 3);
    const overviewWeeklyItems = Array.isArray(weeklyPlan?.items) ? weeklyPlan.items.slice(0, 2) : [];
    const activityWeeklyItems = Array.isArray(weeklyPlan?.items) ? weeklyPlan.items.slice(overviewWeeklyItems.length) : [];
    const detailItems = buildStudentDetailItems(studentDashboard);

    return (
      <>
        <section className="card dashboard-panel col-span-12 dashboard-student-details-panel">
          <div className="dashboard-student-details-strip" aria-label="Student details">
            <div className="dashboard-student-details-grid">
              {detailItems.map((item) => (
                <div key={item.key} className="dashboard-student-detail">
                  <span className="dashboard-student-detail-label">{item.label}</span>
                  <strong className="dashboard-student-detail-value">{item.value}</strong>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="card dashboard-panel col-span-12 dashboard-next-action-panel">
          <div className="section-header">
            <div>
              <p className="eyebrow">Right now</p>
              <h2>Next action</h2>
            </div>
            {displayedNextAction?.priority_label ? (
              <span className="status-pill weekly-practice-plan-pill-progress">{displayedNextAction.priority_label}</span>
            ) : null}
          </div>
          {isLoading ? <p>Loading your dashboard...</p> : null}
          {!isLoading && errorMessage ? <p role="alert" className="notice notice-error">{errorMessage}</p> : null}
          {!isLoading && !errorMessage && displayedNextAction ? (
            <div className="dashboard-next-action-card">
              <p className="dashboard-action-kicker">Recommended next step</p>
              <h3>{displayedNextAction.title}</h3>
              {displayedNextAction.focus_area ? (
                <p className="dashboard-next-action-focus">
                  <span>Focus area</span>
                  <strong>{formatSkill(displayedNextAction.focus_area)}</strong>
                </p>
              ) : null}
              <p className="dashboard-next-action-reason">{displayedNextAction.reason}</p>
              {!activeSession ? (
                <SessionLengthSelector
                  value={sessionLength}
                  onChange={setSessionLength}
                  name="dashboard-student-session-length"
                  className="session-length-selector-compact"
                  description=""
                />
              ) : null}
              <button
                type="button"
                className="btn btn-primary"
                onClick={activeSession
                  ? () => navigate(getSessionPath(role, activeSession))
                  : handleStartStudentPractice}
                disabled={isCreatingStudentPractice || (!activeSession && nextAction?.available === false)}
              >
                {isCreatingStudentPractice
                  ? 'Starting...'
                  : (displayedNextAction.cta_label || 'Start practice')}
              </button>
            </div>
          ) : null}
          {studentPracticeErrorMessage ? (
            <p role="alert" className="notice notice-error">{studentPracticeErrorMessage}</p>
          ) : null}
          <ActiveSessionConflictPrompt
            conflict={studentSessionConflict.conflict}
            isResolving={studentSessionConflict.isResolving}
            onResume={studentSessionConflict.resume}
            onEndAndStartNew={studentSessionConflict.endAndStartNew}
            titleId="dashboard-student-active-session-conflict-title"
          />
        </section>

        <section className="dashboard-panel col-span-12 dashboard-student-lower">
          <div className="dashboard-tab-shell">
              <div className="dashboard-tab-header">
              <div>
                <p className="eyebrow">Dashboard view</p>
              </div>
              <div className="dashboard-tablist" role="tablist" aria-label="Student dashboard views">
                <button
                  type="button"
                  role="tab"
                  aria-selected={studentDashboardView === 'overview'}
                  aria-controls="student-dashboard-overview"
                  className={`dashboard-tab${studentDashboardView === 'overview' ? ' is-active' : ''}`}
                  onClick={() => setStudentDashboardView('overview')}
                >
                  Overview
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={studentDashboardView === 'activity'}
                  aria-controls="student-dashboard-activity"
                  className={`dashboard-tab${studentDashboardView === 'activity' ? ' is-active' : ''}`}
                  onClick={() => setStudentDashboardView('activity')}
                >
                  Activity
                </button>
              </div>
            </div>

            {studentDashboardView === 'overview' ? (
              <div id="student-dashboard-overview" role="tabpanel" className="dashboard-tab-panel">
                <section className="card dashboard-panel col-span-7 dashboard-student-card dashboard-progress-panel">
                  <div className="section-header">
                    <div>
                      <p className="eyebrow">Progress</p>
                      <h2>Your progress</h2>
                    </div>
                  </div>
                  {isLoading ? <p>Loading progress...</p> : null}
                  {!isLoading && !errorMessage && skillProgress ? (
                    <div className="dashboard-progress-stack">
                      <div>
                        <p className="section-title">{skillProgress.headline}</p>
                        <p className="muted-text">{skillProgress.detail}</p>
                      </div>
                      <div className="dashboard-skill-bucket-grid">
                        {(Array.isArray(skillProgress.buckets) ? skillProgress.buckets : []).map((bucket) => (
                          <article
                            key={bucket.key}
                            className={`dashboard-skill-bucket ${skillBucketToneClass(bucket.tone)}`}
                          >
                            <span className="dashboard-action-kicker">{bucket.title}</span>
                            <strong>{bucket.count}</strong>
                            <p>{bucket.detail}</p>
                            {bucket.skill_labels?.length ? (
                              <p className="muted-text">{bucket.skill_labels.join(', ')}</p>
                            ) : null}
                          </article>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </section>

                <section className="card dashboard-panel col-span-5 dashboard-student-card dashboard-weekly-plan-panel">
                  <div className="section-header">
                    <div>
                      <p className="eyebrow">This week</p>
                      <h2>Weekly plan</h2>
                    </div>
                    {weeklyPlan ? (
                      <span className="status-pill weekly-practice-plan-pill-progress">
                        {studentPlanStatusLabel(weeklyPlan.status)}
                      </span>
                    ) : null}
                  </div>
                  {isLoading ? <p>Loading weekly plan...</p> : null}
                  {!isLoading && !errorMessage && weeklyPlan ? (
                    <div className="dashboard-weekly-plan">
                      <div className="dashboard-weekly-plan-summary">
                        <div className="dashboard-progress-meter" aria-label="Weekly plan progress">
                          <strong>{weeklyPlan.completion_percent}%</strong>
                          <span>{weeklyPlan.progress_summary}</span>
                        </div>
                        <p className="muted-text">{weeklyPlan.forward_copy}</p>
                        <p className="muted-text">{weeklyPlan.detail}</p>
                      </div>
                      <ul className="weekly-practice-plan-list weekly-practice-plan-list-compact">
                        {overviewWeeklyItems.map((item) => {
                          const isCurrent = weeklyPlan.current_item_key === item.key;
                          const isNext = !isCurrent && weeklyPlan.next_item_key === item.key;
                          return (
                            <li key={item.key} className="weekly-practice-plan-item">
                              <div className="weekly-practice-plan-item-header">
                                <strong>{item.title}</strong>
                                <div className="dashboard-plan-item-badges">
                                  {isCurrent ? <span className="status-pill weekly-practice-plan-pill-progress">Current</span> : null}
                                  {isNext ? <span className="status-pill weekly-practice-plan-pill-not-started">Next</span> : null}
                                  <span className={`status-pill ${item.status === 'COMPLETE' ? 'weekly-practice-plan-pill-complete' : item.status === 'NOT_STARTED' ? 'weekly-practice-plan-pill-not-started' : 'weekly-practice-plan-pill-progress'}`}>
                                    {studentPlanStatusLabel(item.status)}
                                  </span>
                                </div>
                              </div>
                              <p>{item.detail}</p>
                            </li>
                          );
                        })}
                      </ul>
                      {activityWeeklyItems.length ? (
                        <p className="muted-text">
                          {activityWeeklyItems.length} more focus item{activityWeeklyItems.length === 1 ? '' : 's'} in Activity.
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </section>
              </div>
            ) : (
              <div id="student-dashboard-activity" role="tabpanel" className="dashboard-tab-panel">
                {activityWeeklyItems.length ? (
                  <section className="card dashboard-panel col-span-12 dashboard-student-card dashboard-weekly-plan-panel">
                    <div className="section-header">
                      <div>
                        <p className="eyebrow">This week</p>
                        <h2>Focus items</h2>
                      </div>
                    </div>
                    <ul className="weekly-practice-plan-list weekly-practice-plan-list-compact">
                      {activityWeeklyItems.map((item) => (
                        <li key={item.key} className="weekly-practice-plan-item">
                          <div className="weekly-practice-plan-item-header">
                            <strong>{item.title}</strong>
                            <span className={`status-pill ${item.status === 'COMPLETE' ? 'weekly-practice-plan-pill-complete' : item.status === 'NOT_STARTED' ? 'weekly-practice-plan-pill-not-started' : 'weekly-practice-plan-pill-progress'}`}>
                              {studentPlanStatusLabel(item.status)}
                            </span>
                          </div>
                          <p>{item.detail}</p>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}
                <section className="card dashboard-panel col-span-12 dashboard-student-card">
                  <div className="section-header">
                    <div>
                      <p className="eyebrow">Recent activity</p>
                      <h2>Recent sessions</h2>
                    </div>
                    {recentSessions.length > visibleRecentSessions.length ? (
                      <span className="status-pill weekly-practice-plan-pill-not-started">
                        {visibleRecentSessions.length} shown
                      </span>
                    ) : null}
                  </div>
                  {isLoading ? <p>Loading sessions...</p> : null}
                  {!isLoading && !errorMessage && !recentSessions.length ? (
                    <p className="muted-text">Your recent sessions will appear here once you start practicing.</p>
                  ) : null}
                  {!isLoading && !errorMessage && visibleRecentSessions.length ? (
                    <ul className="session-list session-list-compact">
                      {visibleRecentSessions.map((session) => (
                        <li key={session.session_id} className="session-list-item session-list-item-compact">
                          <Link className="session-list-link session-list-link-compact" to={getSessionPath(role, session)}>
                            <div className="session-list-copy">
                              <strong>{getRecentSessionTitle(session)}</strong>
                              <span>{getRecentSessionSummary(session)}</span>
                            </div>
                            <div className="session-list-meta">
                              <span className={`status-pill ${getSessionStatusTone(session.status)}`}>
                                {formatSessionStatus(session)}
                              </span>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </section>
              </div>
            )}
          </div>
        </section>
      </>
    );
  };

  return (
    <div className="page page-shell dashboard-page">
      <section className={`dashboard-hero card${isStudent ? ' dashboard-hero-student' : ''}`}>
        <div className="dashboard-hero-copy">
          <p className="eyebrow">{isStudent ? 'Today' : 'Dashboard'}</p>
          <h1>{hero.title}</h1>
          <p className="dashboard-hero-subtitle">{hero.subtitle}</p>
        </div>
        <div className={`dashboard-metric-grid${isStudent ? ' dashboard-metric-grid-compact' : ''}`}>
          {metrics.map((metric) => (
            <article key={metric.label} className={`dashboard-metric dashboard-metric-${metric.tone}`}>
              <span className="dashboard-metric-label">{metric.label}</span>
              <strong className="dashboard-metric-value">{metric.value}</strong>
            </article>
          ))}
        </div>
      </section>

      <section className={`dashboard-grid${role === 'TUTOR' || role === 'PARENT' ? ' dashboard-grid-single-column' : ''}`}>
        {isStudent ? renderStudentDashboard() : null}
        {isStudent ? (
          <section className="card dashboard-panel"><p className="eyebrow">Tutoring</p><h2>Tutor link requests</h2><p>Review requests from tutors you already know.</p><Link className="btn btn-secondary" to="/tutor-links">Review tutor requests</Link></section>
        ) : null}

        {isAdmin ? (
          <section className="card dashboard-panel">
            <div className="section-header">
              <div>
                <p className="eyebrow">Primary</p>
                <h2>Next actions</h2>
              </div>
            </div>
            <div className="dashboard-action-list">
              <Link className="dashboard-action-card" to="/admin/users">
                <span className="dashboard-action-kicker">Operations</span>
                <strong>Admin: Users</strong>
                <span>Manage creation, linking, and invite follow-up.</span>
              </Link>
              <Link className="dashboard-action-card" to="/admin/audit">
                <span className="dashboard-action-kicker">Review</span>
                <strong>Audit log</strong>
                <span>Check recent onboarding and staff-side actions.</span>
              </Link>
              <Link className="dashboard-action-card" to="/admin/health">
                <span className="dashboard-action-kicker">Platform</span>
                <strong>System Health</strong>
                <span>
                  Operational status
                  {healthAlertBadge ? ` • ${healthAlertBadge}` : ' • Stable'}
                </span>
              </Link>
              <Link className="dashboard-action-card" to="/analytics/mastery">
                <span className="dashboard-action-kicker">Analytics</span>
                <strong>Mastery Analytics</strong>
                <span>Admin-only progression and calibration reporting.</span>
              </Link>
            </div>
          </section>
        ) : null}

        {role === 'TUTOR' ? (
          <>
            <section className="card dashboard-panel">
              <div className="section-header">
                <div>
                  <p className="eyebrow">Primary</p>
                  <h2>Next actions</h2>
                </div>
              </div>
              <div className="dashboard-action-list">
                <Link className="dashboard-action-card dashboard-action-card-featured" to="/tutor/students">
                  <span className="dashboard-action-kicker">Teaching</span>
                  <strong>Go to My students</strong>
                  <span>Start tutor sessions, review linked students, and follow onboarding remediation.</span>
                </Link>
                <Link className="dashboard-action-card" to="/tutor-links"><span className="dashboard-action-kicker">Linking</span><strong>Link an existing student</strong><span>Send an approval request using the student’s email.</span></Link>
              </div>
            </section>
            <section className="card dashboard-panel">
              <div className="section-header">
                <div>
                  <p className="eyebrow">Students</p>
                  <h2>Tutor sessions</h2>
                </div>
              </div>
              <p>Start tutor sessions from My students.</p>
              <p className="muted-text">Use the student list as the canonical entry point for session start and onboarding follow-up.</p>
              <p>
                <Link className="btn btn-primary" to="/tutor/students">Go to My students</Link>
              </p>
            </section>
          </>
        ) : null}

        {role === 'PARENT' ? (
          <>
            <section className="card dashboard-panel">
              <div className="section-header">
                <div>
                  <p className="eyebrow">Primary</p>
                  <h2>Next actions</h2>
                </div>
              </div>
              <div className="dashboard-action-list">
                <Link className="dashboard-action-card" to="/parent/students">
                  <span className="dashboard-action-kicker">Family</span>
                  <strong>Go to My children</strong>
                  <span>View linked students and parent-safe progress summaries.</span>
                </Link>
                <Link className="dashboard-action-card" to="/tutor-links"><span className="dashboard-action-kicker">Safeguarding</span><strong>Tutor approvals</strong><span>Approve tutor links accepted by your child.</span></Link>
              </div>
            </section>
            <section className="card dashboard-panel">
              <div className="section-header">
                <div>
                  <p className="eyebrow">Access</p>
                  <h2>Parent access</h2>
                </div>
              </div>
              <p className="muted-text">Session start is managed by tutors and admins.</p>
              <p>
                <Link className="btn btn-secondary" to="/parent/students">Go to My children</Link>
              </p>
            </section>
          </>
        ) : null}

        {!isStudent ? (
          <section className="card dashboard-panel">
            <div className="section-header">
              <div>
                <p className="eyebrow">Activity</p>
                <h2>Recent sessions</h2>
              </div>
            </div>
            {isLoading ? <p>Loading sessions...</p> : null}
            {errorMessage ? <p role="alert" className="notice notice-error">{errorMessage}</p> : null}
            {!isLoading && !errorMessage && !recentSessions.length ? (
              <p className="muted-text">No sessions available yet.</p>
            ) : null}
            {!isLoading && !errorMessage && recentSessions.length ? (
              <ul className="session-list">
                {recentSessions.map((session) => {
                  const sessionId = session.session_id ?? session.id ?? '';
                  return (
                    <li key={sessionId || `${session.mode}-${session.status}`} className="session-list-item">
                      <Link className="session-list-link" to={getSessionPath(role, session)}>
                        <div className="session-list-copy">
                          <strong>{getRecentSessionTitle(session)}</strong>
                          {(isAdmin || role === 'TUTOR') && session.student_display_name ? (
                            <span>{session.student_display_name}</span>
                          ) : null}
                          <span>{getRecentSessionSummary(session)}</span>
                        </div>
                        <div className="session-list-meta">
                          <span className={`status-pill ${getSessionStatusTone(session.status)}`}>
                            {formatSessionStatus(session)}
                          </span>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </section>
        ) : null}

        {isAdmin ? (
          <section className="card dashboard-panel dashboard-panel-wide">
            <div className="section-header">
              <div>
                <p className="eyebrow">Admin tools</p>
                <h2>Start Tutor Practice</h2>
              </div>
            </div>
            <p className="muted-text">Admin-only: start a tutor session for a given student/tutor.</p>
            <form onSubmit={handleCreateTutorSession} className="stack-md">
              <SessionLengthSelector
                value={sessionLength}
                onChange={setSessionLength}
                name="dashboard-admin-session-length"
                className="session-length-selector-compact"
              />
              <div>
                <label htmlFor="start-student-id">student_id</label>
                <input
                  id="start-student-id"
                  type="number"
                  value={studentId}
                  onChange={(event) => setStudentId(event.target.value)}
                />
              </div>
              <div>
                <label htmlFor="start-tutor-user-id">tutor_user_id</label>
                <input
                  id="start-tutor-user-id"
                  type="number"
                  value={tutorUserId}
                  onChange={(event) => setTutorUserId(event.target.value)}
                />
              </div>
              <div>
                <label htmlFor="start-track">track</label>
                <input
                  id="start-track"
                  type="text"
                  value={track}
                  onChange={(event) => setTrack(event.target.value)}
                />
              </div>
              <div>
                <label htmlFor="start-tier">tier</label>
                <select
                  id="start-tier"
                  value={tier}
                  onChange={(event) => setTier(event.target.value)}
                >
                  <option value="FOUNDATION">FOUNDATION</option>
                  <option value="HIGHER">HIGHER</option>
                </select>
              </div>
              <div>
                <label htmlFor="start-unit">unit</label>
                <input
                  id="start-unit"
                  type="text"
                  value={unit}
                  onChange={(event) => setUnit(event.target.value)}
                />
              </div>
              <div>
                <label htmlFor="start-topic-id">topic_id (optional)</label>
                <input
                  id="start-topic-id"
                  type="number"
                  value={topicId}
                  onChange={(event) => setTopicId(event.target.value)}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={isCreatingSession}>
                {isCreatingSession ? 'Starting...' : 'Start Tutor Practice'}
              </button>
            </form>
            {createErrorMessage ? <p role="alert" className="notice notice-error">{createErrorMessage}</p> : null}
            <ActiveSessionConflictPrompt
              conflict={adminSessionConflict.conflict}
              isResolving={adminSessionConflict.isResolving}
              onResume={adminSessionConflict.resume}
              onEndAndStartNew={adminSessionConflict.endAndStartNew}
              titleId="dashboard-admin-active-session-conflict-title"
            />
          </section>
        ) : null}
      </section>
      {canRenderDebug && DebugUnlock ? (
        <Suspense fallback={null}>
          <DebugUnlock />
        </Suspense>
      ) : null}
    </div>
  );
}

export default DashboardPage;
